import React, { useState } from "react";
import type {
  ModelImageRow,
  Vehicle,
  VehicleViewData,
  VehicleViewProps
} from "@dealer-center/inventory-pages-gvm-interface";

import { Button, Switch, Tabs } from "@dealer-center/core-components";
import { VehicleDetailView } from "./VehicleDetailView";
import { VehicleMapTable } from "./VehicleMapTable";
import {
  useEvictCache,
  useShowroomStateUpdate,
  useVehicleSave
} from "@dealer-center/inventory-pages-gvm-fetch";
import { useIsGvmRwAccessUser } from "../../util/gvmUserUtil";
import { formatLocale } from "../../util/localeFormatter";
import { toVehicleViewData } from "../../util/vehicleDataMapper";
import { modelMetadata } from "@dealer-center/inventory-core-utils";
import { ColorTable, ImageTable } from "../../Common";

const excludedFromChanges = ["vehicleId", "locale"];

function initVehicleToSave(
  vehicleViewData: VehicleViewData,
  locale: string,
  vehicleId: string
): Record<string, string> {
  const vehicle: Record<string, string> = { locale, vehicleId };
  for (const key in vehicleViewData.validFields) {
    vehicle[key] = vehicleViewData.editItem[key as keyof Vehicle]
      ? String(vehicleViewData.editItem[key as keyof Vehicle])
      : "";
  }
  return vehicle;
}

export const VehicleView = ({
  locale,
  vehicleId,
  closeVehicleView,
  vehicleWithDetailsQuery
}: VehicleViewProps) => {
  const isGvmRwAccessUser = useIsGvmRwAccessUser();
  const [activeTab, setActiveTab] = useState("details");
  const vehicleViewData: VehicleViewData = toVehicleViewData(
    vehicleWithDetailsQuery.data
  );
  const [vehicleToSave, setVehicleToSave] = useState<Record<string, string>>(
    () => initVehicleToSave(vehicleViewData, locale, vehicleId)
  );
  const saveMutation = useVehicleSave();
  const flushMutation = useEvictCache();
  const updateModelShowroomMutation = useShowroomStateUpdate(
    !vehicleWithDetailsQuery.data?.includedInShowroom
  );
  const [selectedImageRows, setSelectedImageRows] = useState<ModelImageRow[]>(
    []
  );

  const originalVehicle = vehicleWithDetailsQuery.data?.originalVehicle.live;
  const formattedLocale = formatLocale(locale);
  const isReadyToSave = Object.keys(vehicleViewData.validFields).every(field =>
    field === "year"
      ? vehicleToSave[field]?.trim().length === 4
      : vehicleToSave[field]?.length <= modelMetadata.length(field, 20) &&
        vehicleToSave[field]?.trim() !== ""
  );

  const isShowRefreshing = () => {
    return (
      saveMutation.isPending ||
      flushMutation.isPending ||
      updateModelShowroomMutation.isPending ||
      vehicleWithDetailsQuery.isFetching
    );
  };

  const isElementDisabled =
    !isGvmRwAccessUser || isShowRefreshing() || !isReadyToSave;

  let saveLabel = "";

  if (vehicleViewData.qa) {
    saveLabel = saveMutation.isPending ? "Promoting..." : "Promote Changes";
  } else {
    saveLabel = saveMutation.isPending ? "Saving..." : "Save Changes";
  }

  const handleTabClick = (tabValue: string) => {
    setActiveTab(tabValue);
  };

  const handleVehicleSave = () => {
    saveMutation.mutate(vehicleToSave);
  };

  const handleVehicleFlush = () => {
    flushMutation.mutate({
      vehicleIds: [vehicleId],
      trimIds: []
    });
  };

  const handleUpdateModelShowroom = () => {
    updateModelShowroomMutation.mutate({
      vehicleId,
      make: originalVehicle?.make || "",
      locale
    });
  };

  function hasChanges() {
    for (const key in vehicleToSave) {
      if (
        vehicleToSave[key] != vehicleViewData.editItem[key as keyof Vehicle] &&
        !excludedFromChanges.includes(key)
      ) {
        return true;
      }
    }
    return false;
  }

  const render = () => {
    return (
      <div style={{ position: "relative", width: "100%" }}>
        <div style={{ textAlign: "left" }}>
          <h2>
            {originalVehicle?.year} {originalVehicle?.make}{" "}
            {originalVehicle?.model} {originalVehicle?.bodyStyle}
          </h2>
          <p style={{ marginTop: "25px" }}>
            <b>Vehicle Id:</b> {vehicleId}
          </p>
          <p>
            <b>Locale:</b> {formattedLocale}
          </p>
          <p>
            <b>Status:</b>{" "}
            {vehicleWithDetailsQuery.data?.active ? "Active" : "Inactive"}
          </p>
          <p>
            <b>In Showroom:</b>{" "}
            <Switch
              checked={vehicleWithDetailsQuery.data?.includedInShowroom}
              disabled={isElementDisabled}
              layout="vertical"
              onClick={handleUpdateModelShowroom}
            />
          </p>
        </div>

        <Tabs
          id="VehicleTabs"
          tabs={[
            {
              component: (
                <VehicleDetailView
                  isLoading={isShowRefreshing()}
                  setVehicleToSave={setVehicleToSave}
                  vehicleToSave={vehicleToSave}
                  vehicleViewData={vehicleViewData}
                />
              ),
              label: "Details",
              value: "details",
              active: activeTab === "details"
            },
            {
              component: (
                <VehicleMapTable
                  isLoading={isShowRefreshing()}
                  vehicleId={vehicleId}
                  vehicleMapList={
                    vehicleWithDetailsQuery.data?.vehicleMapList || []
                  }
                />
              ),
              label: "Mapping",
              value: "mapping",
              active: activeTab === "mapping"
            },
            {
              component: (
                <ImageTable
                  images={vehicleWithDetailsQuery.data?.vehicleImageList ?? []}
                  isLoading={isShowRefreshing()}
                  setSelectedImageRows={setSelectedImageRows}
                  shotTypeLookup={vehicleWithDetailsQuery.data?.shotTypes ?? {}}
                />
              ),
              label: "Images",
              value: "images",
              active: activeTab === "images",
              "data-testid": "imagesTab"
            },
            {
              component: (
                <ColorTable
                  colors={vehicleWithDetailsQuery.data?.vehicleColorList ?? []}
                  isLoading={isShowRefreshing()}
                  shotTypeLookup={vehicleWithDetailsQuery.data?.shotTypes ?? {}}
                />
              ),
              label: "Colors",
              value: "colors",
              active: activeTab === "colors",
              "data-testid": "colorsTab"
            }
          ]}
          onActivate={handleTabClick}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginTop: "20px"
          }}
        >
          <Button
            analyticsConfiguration={{
              eventName: "Close vehicle view clicked"
            }}
            buttonStyle="tertiary"
            data-testid="vehicle-edit-cancel"
            type="button"
            onClick={closeVehicleView}
          >
            Back
          </Button>
          <Button
            analyticsConfiguration={{
              eventName: "Flush caches clicked"
            }}
            buttonStyle="secondary"
            data-testid="vehicle-edit-flush"
            disabled={isElementDisabled}
            type="button"
            onClick={handleVehicleFlush}
          >
            {flushMutation.isPending ? "Flushing..." : "Flush Caches"}
          </Button>
          {activeTab === "details" && (
            <Button
              analyticsConfiguration={{
                eventName: "Save changes clicked"
              }}
              buttonStyle="primary"
              data-testid="vehicle-edit-save"
              disabled={isElementDisabled || !hasChanges()}
              type="button"
              onClick={handleVehicleSave}
            >
              {saveLabel}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return render();
};
